import { motion } from "framer-motion";
import { TrendUp, TrendDown, Minus } from "@phosphor-icons/react";

/**
 * Operational KPI card for the coach dashboard.
 *
 * Props:
 *   - icon: Phosphor icon component
 *   - label: short uppercase title
 *   - value: main number/string to display
 *   - suffix: optional unit (e.g. "%")
 *   - sublabel: short secondary explanation
 *   - delta: numeric delta vs previous period (positive/negative/zero)
 *   - deltaLabel: optional override for the displayed delta string
 *   - direction: "up_is_good" | "down_is_good" — chooses red/green per direction
 *   - accent: accent hex color for icon and value
 *   - onClick: callback to navigate to filtered page
 *   - testId: data-testid suffix
 */
export default function KpiCard({
  icon: Icon,
  label,
  value,
  suffix = "",
  sublabel,
  delta = 0,
  deltaLabel,
  direction = "up_is_good",
  accent = "#007AFF",
  onClick,
  testId,
  index = 0,
}) {
  const isPositive = delta > 0;
  const isNegative = delta < 0;
  const isFlat = delta === 0;
  // Color: green if "good", red if "bad", zinc if flat
  let trendColor = "text-zinc-500";
  if (!isFlat) {
    const goodWhenUp = direction === "up_is_good";
    const isGood = (isPositive && goodWhenUp) || (isNegative && !goodWhenUp);
    trendColor = isGood ? "text-[#34C759]" : "text-red-400";
  }
  const TrendIcon = isFlat ? Minus : isPositive ? TrendUp : TrendDown;
  const deltaTxt =
    deltaLabel ??
    (isFlat ? "stabile" : `${isPositive ? "+" : ""}${delta}${suffix === "%" ? " pt" : ""}`);

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      onClick={onClick}
      className="stat-card text-left w-full hover:border-[#333] hover:bg-[#1a1a1a]/60 transition-colors group"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="label-overline">{label}</span>
        {Icon && <Icon size={22} weight="duotone" style={{ color: accent }} />}
      </div>
      <div className="flex items-baseline gap-1.5">
        <p
          className="text-3xl sm:text-4xl font-black text-white leading-none"
          style={{ fontFamily: "Barlow Condensed, sans-serif", color: accent }}
        >
          {value}
        </p>
        {suffix && (
          <span
            className="text-xl font-bold"
            style={{ fontFamily: "Barlow Condensed, sans-serif", color: accent }}
          >
            {suffix}
          </span>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between">
        {sublabel && (
          <span className="text-[11px] text-zinc-500 truncate pr-2">
            {sublabel}
          </span>
        )}
        <span
          className={`text-[11px] font-semibold flex items-center gap-1 flex-shrink-0 ${trendColor}`}
          data-testid={`${testId}-trend`}
        >
          <TrendIcon size={12} weight="bold" />
          {deltaTxt}
        </span>
      </div>
    </motion.button>
  );
}
